import React, { useState, useEffect } from 'react';
import { TextField, Select } from '@anyemp/ui-kit';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, Eye, EyeOff, Loader } from 'lucide-react';
import { accounts, libraries } from '../../services/apiService';
import toast from 'react-hot-toast';

const CreateMyAccountsModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  const { data: toolsData } = useQuery({
    queryKey: ['tools'],
    queryFn: libraries.getTools,
    enabled: isOpen
  });

  const { data: statusesData } = useQuery({
    queryKey: ['gateway-statuses'],
    queryFn: libraries.getStatuses,
    enabled: isOpen
  });

  const tools = toolsData?.data || [];
  const allStatuses = statusesData?.data || [];

  const pendingStatus = allStatuses.find(s => s.name?.toLowerCase() === 'pending');
  const defaultStatusId = pendingStatus ? pendingStatus.id : null;

  const gmailTool = tools.find(t => t.name?.toLowerCase() === 'gmail');

  const [formData, setFormData] = useState({
    name: '',
    login: '',
    link: '',
    password: '',
    notes: ''
  });

  const resetForm = () => {
    setFormData({ name: '', login: '', link: '', password: '', notes: '' });
  };

  useEffect(() => {
    resetForm();
    setShowPassword(false);
  }, [isOpen]);

  const createMutation = useMutation({
    mutationFn: () => accounts.createAccountOnly({
      tool_id: gmailTool?.id,
      tool_code: gmailTool?.code || gmailTool?.name?.slice(0, 3).toUpperCase(),
      status_id: defaultStatusId,
      ...formData
    }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Account created successfully!');
        queryClient.invalidateQueries(['myAccounts']);
        onClose();
        resetForm();
      } else {
        toast.error(response.error || 'Failed to create account');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create account');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!gmailTool) {
      toast.error('Gmail tool not found');
      return;
    }

    if (!formData.name && !formData.login) {
      toast.error('Name or Login is required');
      return;
    }

    createMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={onClose} ariaLabel="Create account">
      <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none dark:bg-dark-bg-primary sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        <ModalFrame.Header className="items-center">
          <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
            Create Account
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        <ModalFrame.Body className="space-y-4">
          <div>
            <div className="flex items-end gap-2">
              <Select
                label="Tool"
                fullWidth
                disabled
                placeholder="Loading..."
                value={gmailTool?.id ? String(gmailTool.id) : ''}
                options={tools.map((tool) => ({ value: String(tool.id), label: tool.name }))}
              />
            </div>
            {gmailTool ? (
              <span className="text-xs text-green-600 dark:text-green-400 whitespace-nowrap">✓ Gmail</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400 whitespace-nowrap">⚠ Not found</span>
            )}
          </div>

          <div>
            <TextField
              label="Name"
              fullWidth
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Login"
              fullWidth
              type="text"
              value={formData.login}
              onChange={(e) => setFormData({ ...formData, login: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Link"
              fullWidth
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Password"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              endAdornment={(
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary hover:bg-light-bg-secondary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            />
          </div>

          <div>
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg transition-colors"
            >
              {createMutation.isPending && <Loader className="h-4 w-4 animate-spin" />}
              <span>{createMutation.isPending ? 'Creating...' : 'Create'}</span>
            </button>
        </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default CreateMyAccountsModal;
